import { useCallback, useId, useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";

const GAP_PX = 8;
const MARGIN_PX = 8;

const HIDDEN: CSSProperties = { top: 0, left: 0, visibility: "hidden" };

/**
 * Estado do card (em portal) com as obras e datas do conflito, ancorado a um elemento. O card vive em
 * `document.body` com `position: fixed`, então o `overflow` do scroller do Gantt não o corta. Abre no hover/foco.
 */
export function useConflictCard() {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<CSSProperties>(HIDDEN);
  const tooltipId = useId();

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    const card = cardRef.current;
    if (!anchor || !card) return;

    const rect = anchor.getBoundingClientRect();
    const { width, height } = card.getBoundingClientRect();
    const below = rect.bottom + GAP_PX;
    // Abre acima quando não cabe embaixo (e cabe em cima).
    const top =
      below + height > window.innerHeight && rect.top - GAP_PX - height >= 0
        ? rect.top - GAP_PX - height
        : below;
    const left = Math.max(MARGIN_PX, Math.min(rect.left, window.innerWidth - width - MARGIN_PX));
    setPosition({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, updatePosition]);

  const show = () => {
    setPosition(HIDDEN);
    setOpen(true);
  };
  const hide = () => setOpen(false);

  return {
    anchorProps: {
      ref: anchorRef,
      tabIndex: 0,
      "aria-describedby": open ? tooltipId : undefined,
      onMouseEnter: show,
      onMouseLeave: hide,
      onFocus: show,
      onBlur: hide,
    },
    card: { open, id: tooltipId, style: position, cardRef },
  };
}
